const base = import.meta.env.VITE_API_BASE ?? '/api';

// Hard ceiling on any single fetch. Backend can slow-hang for a lot of
// reasons (broken tsx-watch, cold-start Function App, network flake); we'd
// rather show an error than leave the UI spinning forever.
const FETCH_TIMEOUT_MS = 20_000;

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

// AuthContext registers a callback here on mount. When any request returns
// 401 we invoke it so the auth layer can re-auth or bounce to /login — no
// per-caller error handling required.
type OnUnauthorized = () => void;
let onUnauthorized: OnUnauthorized | null = null;
export function setUnauthorizedHandler(fn: OnUnauthorized | null) {
  onUnauthorized = fn;
}

// Callers can opt out of the 401 auto-handler (e.g. /portal/me during boot
// wants to see the 401 to decide whether to fall through to a client-cookie
// check instead of triggering staff re-auth).
export interface ApiFetchOptions extends RequestInit {
  suppressUnauthorizedHandler?: boolean;
  timeoutMs?: number;
}

export async function apiFetch<T = unknown>(path: string, token: string | null, init: ApiFetchOptions = {}): Promise<T> {
  const { suppressUnauthorizedHandler, timeoutMs, ...fetchInit } = init;
  const headers = new Headers(fetchInit.headers);
  if (token) headers.set('Authorization', `Bearer ${token}`);
  if (fetchInit.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');

  const controller = new AbortController();
  const t = setTimeout(() => controller.abort(new DOMException('fetch timed out', 'AbortError')), timeoutMs ?? FETCH_TIMEOUT_MS);
  let r: Response;
  try {
    r = await fetch(`${base}${path}`, { ...fetchInit, headers, credentials: 'include', signal: controller.signal });
  } catch (err) {
    if (controller.signal.aborted) {
      throw new ApiError(0, `API ${path} timed out after ${(timeoutMs ?? FETCH_TIMEOUT_MS) / 1000}s`);
    }
    throw err;
  } finally {
    clearTimeout(t);
  }
  if (r.status === 401 && onUnauthorized && !suppressUnauthorizedHandler) {
    onUnauthorized();
  }
  if (!r.ok) {
    const body = await r.text();
    throw new ApiError(r.status, `API ${path} failed: ${r.status}${body ? ` ${body}` : ''}`);
  }
  return r.json() as Promise<T>;
}

/** A file from the API (a PDF, say), as bytes rather than JSON. */
export async function apiFetchBlob(path: string, token: string | null, init: ApiFetchOptions = {}): Promise<Blob> {
  const { suppressUnauthorizedHandler, timeoutMs, ...fetchInit } = init;
  const headers = new Headers(fetchInit.headers);
  if (token) headers.set('Authorization', `Bearer ${token}`);
  const controller = new AbortController();
  const t = setTimeout(() => controller.abort(new DOMException('fetch timed out', 'AbortError')), timeoutMs ?? FETCH_TIMEOUT_MS);
  let r: Response;
  try {
    r = await fetch(`${base}${path}`, { ...fetchInit, headers, credentials: 'include', signal: controller.signal });
  } catch (err) {
    if (controller.signal.aborted) throw new ApiError(0, `API ${path} timed out after ${(timeoutMs ?? FETCH_TIMEOUT_MS) / 1000}s`);
    throw err;
  } finally {
    clearTimeout(t);
  }
  if (r.status === 401 && onUnauthorized && !suppressUnauthorizedHandler) onUnauthorized();
  if (!r.ok) {
    const body = await r.text();
    throw new ApiError(r.status, `API ${path} failed: ${r.status}${body ? ` ${body}` : ''}`);
  }
  return r.blob();
}

export type StaffRole = 'admin' | 'client_admin';

export interface DiscoverResponse {
  method: 'password' | 'sso' | 'either';
  client: { id: string; slug: string; name: string } | null;
  sso: { tenant_id: string; group_id: string; app_client_id: string; display_name: string } | null;
}
export interface Me {
  user: {
    id: string;
    email?: string;
    name?: string;
    role: 'staff' | 'client';
    staffRole?: StaffRole | null;
  };
  clients: Array<{ id: string; slug: string; name: string; modules?: string[]; /** Staff only: modules in staff preview. */ previewModules?: string[] }>;
}
